<template>
  <div class="sql-admin">
    <div class="card">
      <div class="card-header">
        <h4 class="mb-0">
          <i class="fas fa-database mr-2"></i>
          Executor SQL - Administrador
        </h4>
        <p class="text-muted mb-0 mt-2">
          <small>
            <i class="fas fa-exclamation-triangle text-warning mr-1"></i>
            Esta ferramenta permite executar queries SQL diretamente no banco de dados.
            Use com extrema cautela!
          </small>
        </p>
      </div>
      <div class="card-body">
        <!-- Aviso de Segurança -->
        <b-alert variant="danger" show class="mb-4">
          <h5 class="alert-heading">
            <i class="fas fa-shield-alt mr-2"></i>
            Aviso de Segurança
          </h5>
          <p class="mb-0">
            Esta ferramenta é restrita apenas para administradores. Todas as queries executadas
            são registradas em log. Tenha cuidado ao executar comandos que modificam dados.
          </p>
        </b-alert>

        <!-- Formulário de Query -->
        <div class="mb-4">
          <label for="sql-query" class="font-weight-bold">
            <i class="fas fa-code mr-2"></i>
            Query SQL:
          </label>
          <b-form-textarea
            id="sql-query"
            v-model="form.query"
            rows="10"
            placeholder="Digite sua query SQL aqui..."
            class="font-monospace"
            :class="{ 'border-danger': hasError }"
          ></b-form-textarea>
          <small class="text-muted">
            Exemplo: SELECT * FROM "Pessoas" LIMIT 10;
          </small>
        </div>

        <!-- Opções -->
        <div class="mb-4">
          <b-form-checkbox v-model="form.isReadOnly" switch>
            <strong>Modo Read-Only</strong>
            <small class="text-muted d-block">
              Quando ativado, apenas queries SELECT, WITH, SHOW, DESCRIBE ou EXPLAIN são permitidas.
            </small>
          </b-form-checkbox>
        </div>

        <!-- Botões de Ação -->
        <div class="d-flex gap-2 mb-4">
          <b-button
            variant="primary"
            @click="executarQuery"
            :disabled="loading || !form.query.trim()"
          >
            <i class="fas fa-play mr-2"></i>
            Executar Query
          </b-button>
          <b-button
            variant="secondary"
            @click="limparQuery"
            :disabled="loading"
          >
            <i class="fas fa-eraser mr-2"></i>
            Limpar
          </b-button>
          <b-button
            v-if="resultado"
            variant="info"
            @click="exportarResultado"
            :disabled="loading"
          >
            <i class="fas fa-download mr-2"></i>
            Exportar CSV
          </b-button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-4">
          <b-spinner variant="primary" label="Executando query..."></b-spinner>
          <p class="mt-2">Executando query, aguarde...</p>
        </div>

        <!-- Erro -->
        <b-alert v-if="erro" variant="danger" dismissible @dismissed="erro = null" class="mb-4">
          <h5 class="alert-heading">
            <i class="fas fa-exclamation-circle mr-2"></i>
            Erro ao executar query
          </h5>
          <p class="mb-0">{{ erro }}</p>
        </b-alert>

        <!-- Resultado -->
        <div v-if="resultado && !loading">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5>
              <i class="fas fa-table mr-2"></i>
              Resultado
            </h5>
            <div>
              <b-badge variant="success" class="mr-2">
                {{ resultado.rows ? resultado.rows.length : resultado.rowsAffected }} 
                {{ resultado.rows ? 'linha(s)' : 'linha(s) afetada(s)' }}
              </b-badge>
              <b-badge v-if="resultado.message" variant="info">
                {{ resultado.message }}
              </b-badge>
            </div>
          </div>

          <!-- Tabela de Resultados (para SELECT) -->
          <div v-if="resultado.rows && resultado.rows.length > 0" class="table-responsive">
            <table class="table table-striped table-bordered table-hover table-sm">
              <thead class="thead-dark">
                <tr>
                  <th v-for="coluna in resultado.columns" :key="coluna" scope="col">
                    {{ coluna }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in resultado.rows" :key="index">
                  <td v-for="coluna in resultado.columns" :key="coluna">
                    <span v-if="row[coluna] === null" class="text-muted">NULL</span>
                    <span v-else>{{ formatarValor(row[coluna]) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mensagem para queries de modificação -->
          <div v-else-if="resultado.rowsAffected !== undefined" class="alert alert-success">
            <i class="fas fa-check-circle mr-2"></i>
            {{ resultado.message || `Query executada com sucesso. ${resultado.rowsAffected} linha(s) afetada(s).` }}
          </div>

          <!-- Sem resultados -->
          <div v-else class="alert alert-info">
            <i class="fas fa-info-circle mr-2"></i>
            Query executada com sucesso, mas nenhum resultado foi retornado.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SqlAdminService from '@/common/services/sql-admin/sql-admin.service';
import mensagem from '@/common/utils/mensagem';
import store from '@/store/Store';

export default {
  name: 'SqlAdmin',
  data() {
    return {
      form: {
        query: '',
        isReadOnly: true,
      },
      loading: false,
      erro: null,
      resultado: null,
    };
  },
  computed: {
    hasError() {
      return !!this.erro;
    },
    isAdmin() {
      return store.getters.admin;
    },
  },
  mounted() {
    // Verificar se é admin
    if (!this.isAdmin) {
      this.$router.push({ name: 'dashboard' });
      mensagem.erro('Acesso negado. Apenas administradores podem acessar esta página.');
    }
  },
  methods: {
    async executarQuery() {
      if (!this.form.query.trim()) {
        mensagem.aviso('Por favor, digite uma query SQL.');
        return;
      }

      this.loading = true;
      this.erro = null;
      this.resultado = null;

      try {
        const response = await SqlAdminService.executarSql(
          this.form.query,
          this.form.isReadOnly
        );

        if (response.success && response.data) {
          this.resultado = response.data;
          mensagem.sucesso('Query executada com sucesso!');
        } else {
          const errorMsg = response.errors?.[0]?.mensagem 
            || response.errors?.[0]?.message
            || 'Erro ao executar query.';
          this.erro = errorMsg;
          mensagem.erro(errorMsg);
        }
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.[0]?.mensagem 
          || error.response?.data?.errors?.[0]?.message
          || error.response?.data?.message 
          || error.message 
          || 'Erro ao executar query.';
        
        this.erro = errorMessage;
        mensagem.erro(errorMessage);
      } finally {
        this.loading = false;
      }
    },
    limparQuery() {
      this.form.query = '';
      this.erro = null;
      this.resultado = null;
    },
    formatarValor(valor) {
      if (valor === null || valor === undefined) {
        return 'NULL';
      }
      if (typeof valor === 'boolean') {
        return valor ? 'true' : 'false';
      }
      if (typeof valor === 'object') {
        return JSON.stringify(valor);
      }
      return String(valor);
    },
    exportarResultado() {
      if (!this.resultado || !this.resultado.rows || this.resultado.rows.length === 0) {
        mensagem.aviso('Não há dados para exportar.');
        return;
      }

      try {
        // Criar CSV
        const headers = this.resultado.columns.join(',');
        const rows = this.resultado.rows.map(row => {
          return this.resultado.columns.map(col => {
            const value = row[col];
            if (value === null || value === undefined) return '';
            if (typeof value === 'string' && value.includes(',')) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',');
        });

        const csv = [headers, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `sql-result-${new Date().getTime()}.csv`);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        mensagem.sucesso('Resultado exportado com sucesso!');
      } catch (error) {
        mensagem.erro('Erro ao exportar resultado: ' + error.message);
      }
    },
  },
};
</script>

<style scoped>
.sql-admin {
  padding: 20px;
}

.font-monospace {
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
}

.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}

.table th {
  position: sticky;
  top: 0;
  background-color: #343a40;
  color: white;
  z-index: 10;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.gap-2 {
  gap: 0.5rem;
}
</style>

